/**
 * Phase 6.6 — Shared tag renderer for AI output.
 *
 * Extracted from AIAssistant.tsx so the same tag grammar
 * ([[link:type:value]] and [[action:type|label]]) can be rendered
 * consistently by the main chat, suggestion panels on workflow
 * pages, dashboard widgets, etc. The extraction avoids copy-pasting
 * the ~150 lines of rendering logic.
 *
 * Tag grammar:
 *   [[link:candidate:<name>]]
 *   [[link:staff:<name>]]
 *   [[link:job:<title>]]
 *   [[link:facility:<name>]]
 *   [[link:policy:<title>]]
 *   [[action:create_task|<goal>]]
 *   [[action:send_esign|<recipient>]]
 *   [[action:draft_email|<prompt>]]
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi } from '../../lib/api';
import Icon, { type IconName } from '../ui/Icon';

export type EntityType = 'candidate' | 'staff' | 'job' | 'facility' | 'policy';
export type ActionType = 'create_task' | 'send_esign' | 'draft_email';
export type EntityMatch = { id: string; [k: string]: unknown };

const TAG_RE = /\[\[(link|action):([a-z_]+)(?::|\|)([^\]]+)\]\]/g;

type Tok =
  | { kind: 'text'; text: string }
  | { kind: 'link'; entity: EntityType; value: string }
  | { kind: 'action'; action: ActionType; label: string };

export function tokenize(content: string): Tok[] {
  const tokens: Tok[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  TAG_RE.lastIndex = 0;
  while ((m = TAG_RE.exec(content))) {
    if (m.index > lastIdx) tokens.push({ kind: 'text', text: content.slice(lastIdx, m.index) });
    const [, kind, type, rest] = m;
    if (kind === 'link') tokens.push({ kind: 'link', entity: type as EntityType, value: rest.trim() });
    else                 tokens.push({ kind: 'action', action: type as ActionType, label: rest.trim() });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < content.length) tokens.push({ kind: 'text', text: content.slice(lastIdx) });
  if (tokens.length === 0) tokens.push({ kind: 'text', text: content });
  return tokens;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function formatTextSegment(s: string): string {
  return escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
}

// Heroicons via the shared <Icon> name API, never emoji: these pills are
// tinted by `fg`, and an Icon drawn in currentColor re-tints with them (and
// with the tenant brand, and with dark mode). An emoji is a fixed-colour
// glyph that cannot. Names match the sidebar's vocabulary for the same
// things, so a "candidate" pill and the Candidates nav item agree.
//
// `border` is its own token rather than the old `${fg}33` trick — appending
// an alpha pair to a var() only produced a valid colour while every token
// happened to be a 6-digit hex, and silently dropped the whole border the
// moment one became rgb()/color-mix(). The design system ships a matching
// -border for each tint; use it.
const ENTITY_COLORS: Record<EntityType, { bg: string; fg: string; border: string; icon: IconName }> = {
  candidate: { bg: 'var(--info-tint)', fg: 'var(--brand)',   border: 'var(--info-border)', icon: 'candidates' },
  staff:     { bg: 'var(--good-tint)', fg: 'var(--good)',    border: 'var(--good-border)', icon: 'users' },
  job:       { bg: 'var(--warn-tint)', fg: 'var(--warn-fg)', border: 'var(--warn-border)', icon: 'jobs' },
  facility:  { bg: 'var(--ai-tint)',   fg: 'var(--ai)',      border: 'var(--ai-border)',   icon: 'building' },
  // Was a pair of raw pink hex literals, which had no dark-mode counterpart
  // and so rendered near-white-on-pink there. Neutral tokens read correctly
  // on both planes (docs/frontend-standards.md §1-2).
  policy:    { bg: 'var(--surface-subtle)', fg: 'var(--text-strong)', border: 'var(--border)', icon: 'scale' },
};
const ACTION_COLORS: Record<ActionType, { bg: string; fg: string; border: string; icon: IconName }> = {
  create_task:  { bg: 'var(--ai-tint)',   fg: 'var(--ai)',      border: 'var(--ai-border)',   icon: 'clipboard-check' },
  send_esign:   { bg: 'var(--warn-tint)', fg: 'var(--warn-fg)', border: 'var(--warn-border)', icon: 'edit' },
  draft_email:  { bg: 'var(--info-tint)', fg: 'var(--brand)',   border: 'var(--info-border)', icon: 'mail' },
};

function hrefFor(entity: EntityType, match: EntityMatch): string {
  switch (entity) {
    case 'candidate': return `/candidates/${match.id}`;
    case 'staff':     return `/staff/${match.id}`;
    case 'job':       return `/jobs/${match.id}`;
    case 'facility':  return `/clients/${match.id}`;
    case 'policy':    return `/compliance/admin/policies/${match.id}/edit`;
  }
}

// ── Buttons ──────────────────────────────────────────────────────────────

export function EntityLinkButton({ entity, value, onDisambiguate }: {
  entity: EntityType; value: string;
  onDisambiguate: (entity: EntityType, value: string, matches: EntityMatch[]) => void;
}) {
  const navigate = useNavigate();
  const c = ENTITY_COLORS[entity];
  async function onClick(evt: React.MouseEvent) {
    // Phase 5.3 QA fix — log every branch + stop propagation so clicks
    // inside nested containers (e.g. a dashboard card) don't get
    // swallowed by parent handlers. Matches the inline implementation
    // in AIAssistant.tsx.
    evt.preventDefault();
    evt.stopPropagation();
    console.log('[ai-tags] entity pill clicked:', { entity, value });
    try {
      const resp = await aiApi.resolveEntity(entity, value);
      const matches = Array.isArray(resp?.data?.matches) ? resp.data.matches : [];
      console.log('[ai-tags] resolve-entity response:', { entity, value, matchCount: matches.length });
      if (matches.length === 0) { alert(`No ${entity} found matching "${value}".`); return; }
      if (matches.length === 1) { navigate(hrefFor(entity, matches[0])); return; }
      onDisambiguate(entity, value, matches);
    } catch (e: any) {
      console.error('[ai-tags] resolve-entity error:', e);
      alert(e?.response?.data?.error ?? e?.message ?? 'Lookup failed.');
    }
  }
  return (
    <button onClick={onClick} title={`Go to ${entity}: ${value}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px',
        background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
        borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        marginInline: 2, verticalAlign: 'baseline',
      }}>
      <Icon name={c.icon} size={12} />
      {value}
    </button>
  );
}

export function ActionTagButton({ action, label, onInvoke }: {
  action: ActionType; label: string;
  onInvoke: (action: ActionType, label: string) => void;
}) {
  const c = ACTION_COLORS[action];
  const verb: Record<ActionType, string> = { create_task: 'Create task', send_esign: 'Send eSign', draft_email: 'Draft email' };
  return (
    <button onClick={() => onInvoke(action, label)}
      title={`${verb[action]}: ${label}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
        background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
        borderRadius: 14, fontSize: 12, fontWeight: 700, cursor: 'pointer',
        marginInline: 2, verticalAlign: 'baseline',
      }}>
      <Icon name={c.icon} size={13} />
      {verb[action]}: {label}
    </button>
  );
}

// ── DisambiguationModal ──────────────────────────────────────────────────

export function DisambiguationModal({ entity, value, matches, onClose }: {
  entity: EntityType; value: string; matches: EntityMatch[]; onClose: () => void;
}) {
  const navigate = useNavigate();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--surface)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Pick the right {entity}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{matches.length} matches for "{value}"</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {matches.map(m => (
            <button key={String(m.id)} onClick={() => { navigate(hrefFor(entity, m)); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 14px', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{describe(entity, m)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{secondary(entity, m)}</div>
              </div>
              <Icon name="arrow-right" size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
function describe(entity: EntityType, m: EntityMatch): string {
  if (entity === 'candidate' || entity === 'staff') return `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || String(m.id);
  if (entity === 'job' || entity === 'policy') return String(m.title ?? m.id);
  if (entity === 'facility') return String(m.name ?? m.id);
  return String(m.id);
}
function secondary(entity: EntityType, m: EntityMatch): string {
  if (entity === 'candidate') return [m.email, m.role, m.stage].filter(Boolean).join(' · ');
  if (entity === 'staff')     return [m.email, m.role, m.status].filter(Boolean).join(' · ');
  if (entity === 'job')       return String(m.status ?? '');
  // facilities store a single `address`; they have no city/state columns.
  if (entity === 'facility')  return String(m.address ?? '');
  if (entity === 'policy')    return [m.version, m.status].filter(Boolean).join(' · ');
  return '';
}

// ── Renderer ─────────────────────────────────────────────────────────────

export function TaggedText({ content, onInvokeAction, onDisambiguate }: {
  content: string;
  onInvokeAction: (action: ActionType, label: string) => void;
  onDisambiguate: (entity: EntityType, value: string, matches: EntityMatch[]) => void;
}) {
  const tokens = tokenize(content);
  return (
    <>
      {tokens.map((t, i) => {
        if (t.kind === 'text') return <span key={i} dangerouslySetInnerHTML={{ __html: formatTextSegment(t.text) }} />;
        if (t.kind === 'link') return <EntityLinkButton key={i} entity={t.entity} value={t.value} onDisambiguate={onDisambiguate} />;
        return <ActionTagButton key={i} action={t.action} label={t.label} onInvoke={onInvokeAction} />;
      })}
    </>
  );
}

/** Reusable disambig-modal container. Drop next to <TaggedText> and
 *  its onDisambiguate callback sets the state this component reads. */
export function useDisambig() {
  const [state, setState] = useState<{ entity: EntityType; value: string; matches: EntityMatch[] } | null>(null);
  const onDisambiguate = (entity: EntityType, value: string, matches: EntityMatch[]) => setState({ entity, value, matches });
  const element = state
    ? <DisambiguationModal entity={state.entity} value={state.value} matches={state.matches} onClose={() => setState(null)} />
    : null;
  return { onDisambiguate, element };
}
